import React, { useState, useContext, useEffect } from 'react';
import {
  Form,
  Fieldset,
  SelectField,
  Actions,
  Box,
  Loading,
  Pagetitle,
  FriendlyCaptcha,
} from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RequestSubscriptionRequest } from '@ktag/newsletters-sdk/dist';
import uniqid from 'uniqid';
import { useParams } from 'react-router-dom';
import DataPolicy from 'components/dataPolicy/DataPolicy';
import AppContext from 'AppContext';
import { Newsletter, Salutation } from 'types/api.types';
import DynamicFormField from 'components/dynamicFormField/DynamicFormField';
import BackToOverview from 'components/backToOverview/BackToOverview';
import mergeDynamicFormFields from 'utils/mergeDynamicFormFields';
import SubscribeProgress from './SubscribeProgress';

type FormValues = Omit<RequestSubscriptionRequest, 'bksakTheaterfunken'> & {
  bksakTheaterfunken?: 'on' | null;
  friendlyCaptcha?: string;
};

const Subscribe = () => {
  const { t } = useTranslation('newsletter');
  const [dynamicFormFields, setDynamicFormFields] = useState([]);
  const { config, subscriptionApi, newsletterApi, siteKey } = useContext(AppContext);
  const { id: predefinedId } = useParams<{ id: string }>();
  const [subscribeStep, setSubscribeStep] = useState(1);

  const { data, isLoading } = useQuery<Newsletter[]>(['newsletters'], () =>
    newsletterApi.getNewsletters(),
  );

  const initialValues = {
    newsletterIds: [Number(predefinedId)],
  };

  const requestSubscription = useMutation<void, void, RequestSubscriptionRequest>({
    mutationFn: subscriptionApi.requestSubscription.bind(subscriptionApi),
  });

  const handleSubmit = async ({
    bksakTheaterfunken,
    friendlyCaptcha,
    ...rest
  }: FormValues) => {
    const requestSubscriptionRequest: RequestSubscriptionRequest = {
      ...rest,
      captcha: friendlyCaptcha,
    };

    if (!requestSubscriptionRequest.salutation) {
      requestSubscriptionRequest.salutation = Salutation.NoSalutation;
    }

    if (bksakTheaterfunken !== undefined) {
      requestSubscriptionRequest.bksakTheaterfunken = bksakTheaterfunken === 'on';
    }
    requestSubscription.mutate(requestSubscriptionRequest);
    setSubscribeStep(2);
  };

  const parseNewslettersIntoOptions = () => {
    const newsletterOptions = data
      ?.slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => ({
        label: item.name,
        value: item.id,
      }));

    return newsletterOptions;
  };

  const setDynamicFields = (ids: number[]) => {
    const newsletterObjects = parseNewslettersIntoOptions().filter((option) =>
      ids.includes(option.value),
    );

    const updatedDynamicFormFields = config.lists
      .filter((item) => ids.includes(item.id))
      .map(({ id, fields }) =>
        fields.map((field) => {
          const matchingObject = newsletterObjects.find((obj) => obj.value === id);
          return {
            ...field,
            newsletterLabels: matchingObject ? [matchingObject.label] : [],
          };
        }),
      )
      .flat();

    setDynamicFormFields(mergeDynamicFormFields(updatedDynamicFormFields));
  };

  const handleChange = (e) => {
    const selectedIds = e.target.value;
    setDynamicFields(selectedIds);
  };

  useEffect(() => {
    if (!predefinedId || isLoading) return;
    setDynamicFields([Number(predefinedId)]);
  }, [isLoading]);

  return (
    <div className="subscribe richtext">
      <Pagetitle additionalClassNames="subscribe__header">
        {t('subscribe_header')}
      </Pagetitle>
      <SubscribeProgress isSuccess={requestSubscription.isSuccess} />
      {isLoading && <Loading size="medium" />}
      {subscribeStep === 1 && !isLoading && (
        <Form onSubmit={handleSubmit} initialValues={initialValues}>
          <Fieldset>
            <SelectField
              className="subscribe__form--newsletter-select"
              label={t('subscribe_select_newsletter')}
              name="newsletterIds"
              options={parseNewslettersIntoOptions()}
              filterable
              multiple
              onChange={handleChange}
              required
            />
            {config.defaultFields.concat(dynamicFormFields).map((item) => (
              <DynamicFormField fieldItem={item} key={uniqid()} />
            ))}
          </Fieldset>

          <p className="subscribe__form-required-text">
            {t('newsletter_required_fields_text')}
          </p>

          <div className="subscribe__form-captcha-container">
            <FriendlyCaptcha siteKey={siteKey} />
          </div>

          <div className="subscribe__form-footer-container">
            <DataPolicy />
            <Actions label={t('subscribe_subscribe')} />
          </div>
        </Form>
      )}
      {subscribeStep === 2 && (
        <div className="subscribe__confirm-subscription-container">
          {requestSubscription.isLoading && <Loading size="medium" />}
          {requestSubscription.isSuccess && !requestSubscription.isLoading && (
            <Box heading={t('subscribe_infobox_confirm_subscription_title')}>
              <p>{t('subscribe_infobox_confirm_subscription_text')}</p>
            </Box>
          )}
          {!requestSubscription.isSuccess && !requestSubscription.isLoading && (
            <Box
              heading={t('subscribe_infobox_confirm_subscription_failed_title')}
              type="negative"
            >
              <p>{t('subscribe_infobox_confirm_subscription_failed_text')}</p>
            </Box>
          )}
          {!requestSubscription.isLoading && <BackToOverview />}
        </div>
      )}
    </div>
  );
};

export default Subscribe;
